import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ZodEffects, ZodObject, ZodString, ZodTypeAny } from 'zod';
import EditableLabelInput from './EditableLabelInput';
import { EditableLabelSize } from './types';
import { theme } from '../../../../../../styles/theme';

interface IProps {
  value: string;
  onSubmit: (newValue: string) => void;
  disabled?: boolean;
  validation?: ZodObject<
    { value: ZodEffects<ZodString, string, string> },
    'strip',
    ZodTypeAny,
    { value: string },
    { value: string }
  >;
  placeholder?: string;
  size?: EditableLabelSize;
  fontColor?: string;
}

const CurrentValue = styled.button<{
  $size: EditableLabelSize;
  $fontColor?: string;
}>`
  display: inline-block;
  max-width: 100%;
  padding: 2px 4px;
  border-radius: 2px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: #333;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  &:not(:disabled):hover {
    background-color: ${theme.colors.blue500};
    cursor: text;
  }
  &:disabled {
    pointer-events: none;
  }

  ${({ $size, $fontColor }) =>
    $size === 'small' &&
    `
    font-size: 12px;
    font-weight: 600;
    color: ${$fontColor || theme.colors.secondary800};
  `};

  ${({ $size, $fontColor }) =>
    $size === 'large' &&
    `
    font-size: 24px;
    font-weight: 600;
    color: ${$fontColor || theme.colors.secondary800};
  `};
`;

export default ({
  value,
  onSubmit,
  disabled,
  validation,
  placeholder,
  size = 'medium',
  fontColor,
}: IProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentValue, setCurrentValue] = useState('');

  const deactivateEditMode = () => setIsEditMode(false);

  const submit = async (newValue: string) => {
    setSubmitting(true);

    await onSubmit(newValue);

    setSubmitting(false);
  };

  const handleSubmit = (newValue: string) => {
    deactivateEditMode();

    if (newValue !== value) {
      setCurrentValue(newValue);

      submit(newValue);
    }
  };

  const handleClick = () => {
    if (!submitting) {
      setIsEditMode(true);
    }
  };

  useEffect(() => {
    setCurrentValue(value);
  }, [
    value,
    // if edit mode changes, get the latest value prop from parent
    isEditMode,
  ]);

  if (isEditMode) {
    return (
      <EditableLabelInput
        value={currentValue}
        onSubmit={handleSubmit}
        onCancel={deactivateEditMode}
        validation={validation}
        placeholder={placeholder}
        size={size}
        fontColor={fontColor}
      />
    );
  }

  return (
    <CurrentValue
      type="button"
      onClick={handleClick}
      disabled={disabled}
      $size={size}
      $fontColor={fontColor}
    >
      {currentValue || placeholder}
    </CurrentValue>
  );
};
